import { useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Layers, Trash2, ShieldCheck, Check, Phone, Droplets, Settings, Car, Truck, Factory, Cog, Warehouse, HardHat, Landmark, Siren, Package, LifeBuoy, Leaf, Waves } from "lucide-react";
import PageHero from "../components/PageHero";
import BoneDryLabel from "../components/BoneDryLabel";
import Reveal from "../components/Reveal";
import FlyerDownloadButton from "../components/FlyerDownloadButton";
import { absorbentContent, boneDryProducts, boomsContent, company } from "../mock";

const icons = [Zap, Layers, Trash2, ShieldCheck];
const detailIcons = { Droplets, ShieldCheck, Settings, Car, Truck, Factory, Cog, Warehouse, HardHat, Landmark, Siren, Package };
const boomIcons = { LifeBuoy, ShieldCheck, Droplets, Leaf };

export default function Absorbent() {
  const [active, setActive] = useState(boneDryProducts[0].id);
  const product = boneDryProducts.find((p) => p.id === active);

  return (
    <div>
      <PageHero
        kicker="Drying Agent / Absorbent"
        title={absorbentContent.header}
        image={absorbentContent.headerImage}
      />

      {/* Intro */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-[900px] px-6">
          <h2 className="font-heading text-2xl md:text-3xl text-[#1a2540] leading-snug mb-8 text-center">
            {absorbentContent.lead}
          </h2>
          <div className="w-14 h-[3px] bg-[#1b75bc] mx-auto mb-10" />
          {absorbentContent.paragraphs.map((p, i) => (
            <p key={i} className="text-[#6a6a6a] leading-relaxed mb-6">
              {p}
            </p>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#0f6b82]">
        <div className="mx-auto max-w-[1000px] px-6 grid grid-cols-1 sm:grid-cols-3 gap-8">
          {absorbentContent.stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-heading text-5xl md:text-6xl font-light text-white mb-3">{s.value}</p>
              <p className="text-white/80 text-sm leading-relaxed max-w-[240px] mx-auto">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-[#f6f8fa]">
        <div className="mx-auto max-w-[1100px] px-6">
          <div className="text-center mb-14">
            <p className="font-heading uppercase tracking-[0.35em] text-[#1b75bc] text-sm mb-3">
              Why BoneDry™
            </p>
            <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-[0.08em] text-[#1a2540]">
              Superior Performance
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {absorbentContent.features.map((f, i) => {
              const Icon = icons[i % icons.length];
              return (
                <Reveal key={f.title} delay={i * 80}>
                <div className="group msv-card h-full p-8 text-center">
                  <div className="msv-icon w-14 h-14 mx-auto mb-5">
                    <Icon size={26} />
                  </div>
                  <h3 className="font-heading text-base uppercase tracking-[0.04em] text-[#1a2540] mb-3 leading-tight">
                    {f.title}
                  </h3>
                  <p className="text-sm text-[#6a6a6a] leading-relaxed">{f.text}</p>
                </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Product lines */}
      <section className="py-20 msv-dot-bg">
        <div className="mx-auto max-w-[1100px] px-6">
          <div className="text-center mb-12">
            <p className="font-heading uppercase tracking-[0.35em] text-[#1b75bc] text-sm mb-6">
              Product Line
            </p>
            <img
              src="/bonedry/family-logo.png"
              alt="BoneDry Premium Drying Agent — The BoneDry Family"
              className="mx-auto w-full max-w-[520px] md:max-w-[640px] h-auto"
              data-testid="bonedry-family-logo"
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-[620px] mx-auto mb-12">
            {boneDryProducts.map((p) => (
              <button
                key={p.id}
                onClick={() => setActive(p.id)}
                aria-label={p.name}
                className={`flex items-center justify-center h-24 w-full px-5 bg-white rounded-xl border transition-all duration-300 ${
                  active === p.id
                    ? "border-[#1b75bc] shadow-[0_14px_30px_-14px_rgba(27,117,188,0.5)] ring-1 ring-[#1b75bc]/30 -translate-y-0.5"
                    : "border-gray-200 hover:border-[#1b75bc] hover:shadow-md hover:-translate-y-0.5 opacity-70 hover:opacity-100"
                }`}
              >
                {p.logo ? (
                  <img
                    src={p.logo}
                    alt={`${p.name} logo`}
                    className="max-h-16 max-w-full object-contain"
                    loading="lazy"
                  />
                ) : (
                  <BoneDryLabel variant={p.logoLabel} size="md" />
                )}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_10px_30px_-18px_rgba(15,41,74,0.25)] overflow-hidden">
            {product.banner && (
              <div className="bg-gradient-to-r from-[#b3181f] to-[#e2242c] px-8 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                <span className="font-heading uppercase tracking-[0.06em] text-white text-lg">
                  {product.banner}
                </span>
                {product.punch && (
                  <span className="font-heading uppercase tracking-[0.1em] text-[#ffd7a0] text-xs">
                    {product.punch}
                  </span>
                )}
              </div>
            )}
            <div className="p-8 md:p-12">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5 mb-6">
              {product.logo ? (
                <img
                  src={product.logo}
                  alt={`${product.name} logo`}
                  className="h-20 w-auto object-contain"
                />
              ) : (
                <div className="py-1">
                  <BoneDryLabel variant={product.logoLabel} size="lg" />
                </div>
              )}
              <h3 className="font-heading text-2xl uppercase tracking-[0.05em] text-[#1a2540]">
                {product.name}
              </h3>
            </div>
            <div className={product.image ? "grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-10 items-start" : ""}>
              <div>
                <p className="font-heading uppercase tracking-[0.03em] text-[#1b75bc] leading-relaxed mb-6">
                  {product.tagline}
                </p>
                <p className="text-[#6a6a6a] leading-relaxed mb-8">{product.intro}</p>
                <ul className="space-y-4">
                  {product.points.map((point) => (
                    <li key={point} className="flex items-start gap-3">
                      <Check size={18} className="text-[#1b75bc] mt-0.5 shrink-0" />
                      <span className="text-sm text-[#4a4a4a] leading-relaxed">{point}</span>
                    </li>
                  ))}
                </ul>

                {product.skus && (
                  <div className="mt-8 pt-6 border-t border-gray-100">
                    <h4 className="font-heading uppercase tracking-[0.08em] text-sm text-[#1a2540] mb-4">
                      Available Sizes
                    </h4>
                    <div className="overflow-hidden rounded-xl border border-gray-100">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="bg-[#f6f8fa]">
                            <th className="font-heading uppercase tracking-[0.06em] text-xs text-[#1a2540] px-4 py-3">
                              SKU
                            </th>
                            <th className="font-heading uppercase tracking-[0.06em] text-xs text-[#1a2540] px-4 py-3">
                              Description
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {product.skus.map((s) => (
                            <tr key={s.sku} className="border-t border-gray-100">
                              <td className="px-4 py-3 text-sm font-medium text-[#1b75bc]">{s.sku}</td>
                              <td className="px-4 py-3 text-sm text-[#4a4a4a]">{s.description}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {product.image && (
                <div>
                  <div className="rounded-2xl overflow-hidden border border-gray-100 shadow-[0_18px_40px_-20px_rgba(15,41,74,0.4)]">
                    <img
                      src={product.image}
                      alt={`${product.name} product`}
                      className="w-full h-auto object-cover"
                      loading="lazy"
                    />
                  </div>
                  {product.highlights && (
                    <div className="mt-4 rounded-xl p-5 bg-gradient-to-br from-[#1a2540] to-[#0f2c4a]">
                      {product.badge && (
                        <p className="font-heading uppercase tracking-[0.14em] text-[#ff9e2c] text-sm mb-3">
                          {product.badge}
                        </p>
                      )}
                      <ul className="space-y-2">
                        {product.highlights.map((h) => (
                          <li key={h} className="flex items-start gap-2.5">
                            <Check size={15} className="text-[#4aa3e0] mt-0.5 shrink-0" />
                            <span className="text-xs text-gray-200 leading-relaxed">{h}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Absorbs all liquids */}
            {product.absorbs && (
              <div className="mt-10">
                <p className="font-heading uppercase tracking-[0.08em] text-sm text-[#1a2540] mb-4">
                  Absorbs All Liquids
                </p>
                <div className="flex flex-wrap gap-2.5">
                  {product.absorbs.map((l) => (
                    <span
                      key={l}
                      className="inline-flex items-center gap-2 bg-[#eaf3fb] text-[#1b75bc] text-sm font-medium px-4 py-2 rounded-full"
                    >
                      <Droplets size={15} /> {l}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Pillars */}
            {product.pillars && (
              <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
                {product.pillars.map((p) => {
                  const Icon = detailIcons[p.icon] || Droplets;
                  return (
                    <div key={p.title} className="group bg-[#f6f8fa] rounded-xl p-6 border border-gray-100">
                      <div className="msv-icon w-12 h-12 mb-4">
                        <Icon size={22} />
                      </div>
                      <h4 className="font-heading text-base uppercase tracking-[0.03em] text-[#1a2540] mb-2">
                        {p.title}
                      </h4>
                      <p className="text-sm text-[#6a6a6a] leading-relaxed">{p.text}</p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Packaging */}
            {product.packaging && (
              <div className="mt-12 pt-10 border-t border-gray-100">
                <h4 className="font-heading text-xl uppercase tracking-[0.06em] text-[#1a2540] mb-6 text-center">
                  Available Packaging
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {product.packaging.map((pk) => (
                    <div key={pk.name} className="group msv-card p-6 flex flex-col">
                      {pk.image ? (
                        <div className="mb-5 h-40 flex items-center justify-center">
                          <img
                            src={pk.image}
                            alt={`BoneDry ${pk.name}`}
                            className="max-h-40 max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <div className="msv-icon w-12 h-12 mb-4">
                          <Package size={22} />
                        </div>
                      )}
                      <h5 className="font-heading text-lg uppercase tracking-[0.03em] text-[#1a2540] leading-tight">
                        {pk.name}
                      </h5>
                      <p className="text-xs font-heading uppercase tracking-[0.08em] text-[#ff9e2c] mt-1 mb-4">
                        {pk.tag}
                      </p>
                      <ul className="space-y-2.5">
                        {pk.points.map((pt) => (
                          <li key={pt} className="flex items-start gap-2.5">
                            <Check size={15} className="text-[#1b75bc] mt-0.5 shrink-0" />
                            <span className="text-sm text-[#4a4a4a] leading-relaxed">{pt}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Applications */}
            {product.applications && (
              <div className="mt-12 pt-10 border-t border-gray-100">
                <h4 className="font-heading text-xl uppercase tracking-[0.06em] text-[#1a2540] mb-6 text-center">
                  Ideal for Many Applications
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {product.applications.map((a) => {
                    const Icon = detailIcons[a.icon] || Factory;
                    return (
                      <div
                        key={a.label}
                        className="group flex flex-col items-center text-center gap-3 bg-[#f6f8fa] rounded-xl p-5 border border-gray-100 hover:border-[#1b75bc]/30 hover:shadow-md transition-all duration-300"
                      >
                        <div className="msv-icon w-12 h-12">
                          <Icon size={22} />
                        </div>
                        <span className="text-xs font-medium text-[#4a4a4a] leading-tight">{a.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      </section>

      {/* Unsinkable Socks & Booms */}
      <section className="relative py-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${boomsContent.heroImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f3a52]/95 to-[#0f6b82]/92" />
        <div className="relative mx-auto max-w-[1240px] px-6">
          <Reveal className="text-center mb-14">
            <p className="font-heading uppercase tracking-[0.35em] text-[#8fd3e8] text-sm mb-3 flex items-center justify-center gap-3">
              <Waves size={18} /> {boomsContent.eyebrow}
            </p>
            <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-[0.06em] text-white mb-4">
              {boomsContent.title}
            </h2>
            <p className="text-white/80 leading-relaxed max-w-2xl mx-auto">{boomsContent.subtitle}</p>
          </Reveal>

          {/* Features */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
            {boomsContent.features.map((f, i) => {
              const Icon = boomIcons[f.icon] || Droplets;
              return (
                <Reveal key={f.title} delay={i * 80}>
                <div className="h-full bg-white/[0.07] rounded-2xl border border-white/10 p-7 backdrop-blur-sm hover:bg-white/10 hover:-translate-y-1 transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                    <Icon size={22} className="text-[#8fd3e8]" />
                  </div>
                  <h3 className="font-heading text-base uppercase tracking-[0.03em] text-white mb-2 leading-tight">
                    {f.title}
                  </h3>
                  <p className="text-sm text-white/75 leading-relaxed">{f.text}</p>
                </div>
                </Reveal>
              );
            })}
          </div>

          {/* Sizes */}
          <Reveal className="text-center mb-8">
            <h3 className="font-heading text-2xl uppercase tracking-[0.08em] text-white">
              Available in Three Convenient Sizes
            </h3>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {boomsContent.sizes.map((s, i) => (
              <Reveal key={s.name} delay={i * 90}>
              <div className="group h-full bg-white rounded-2xl overflow-hidden shadow-[0_18px_40px_-20px_rgba(0,0,0,0.5)] flex flex-col">
                <div className="h-52 bg-white overflow-hidden flex items-center justify-center">
                  <img
                    src={s.image}
                    alt={`BoneDry ${s.name}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-8 flex flex-col flex-1">
                  <h4 className="font-heading text-xl uppercase tracking-[0.04em] text-[#1a2540] leading-tight">
                    {s.name}
                  </h4>
                  <p className="text-xs font-heading uppercase tracking-[0.08em] text-[#0f6b82] mt-1 mb-4">
                    {s.spec}
                  </p>
                  <p className="text-sm text-[#6a6a6a] leading-relaxed">{s.text}</p>
                </div>
              </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1a2540]">
        <div className="mx-auto max-w-[820px] px-6 text-center">
          <h2 className="font-heading text-3xl uppercase tracking-[0.08em] text-white mb-5">
            Ready to Order BoneDry™?
          </h2>
          <p className="text-gray-300 leading-relaxed mb-8">
            For more information or to place an order, contact Market Street Ventures.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="font-heading uppercase tracking-[0.2em] text-sm text-white bg-[#1b75bc] px-10 py-4 hover:bg-[#155e97] transition-colors duration-300"
            >
              Contact Us
            </Link>
            <FlyerDownloadButton product="absorbent" accent="#0f6b82" accentHover="#0c586b" />
            <a
              href={`tel:${company.phoneRaw}`}
              className="inline-flex items-center gap-2 font-heading uppercase tracking-[0.2em] text-sm text-white/90 hover:text-white transition-colors"
            >
              <Phone size={16} /> {company.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
